import type { I18nLocale } from '#/i18n/types/I18nLocale';
import type { EstimatedRange } from '#/modules/salaries/types/EstimatedRange';
import type { RidgeLineChart } from '#/modules/salaries/types/RidgeLineChart';
import type { RidgeLineData } from '#/modules/salaries/types/RidgeLineData';
import type { NonNullableFields } from '#/types/NonNullableFields';
import { range } from '#/utils/functional/array/range';

const TICK_INTERVAL = 10000;
const MAX_GRID_LINES = 9;

function getMinLowerBound(
  synonyms: NonNullableFields<EstimatedRange>[],
): number {
  return Math.min(...synonyms.map((synonym) => synonym.scatterData.x[0]));
}

function getMaxUpperBound(
  synonyms: NonNullableFields<EstimatedRange>[],
): number {
  return synonyms.reduce(
    (acc, synonym) =>
      Math.max(acc, synonym.scatterData.x[synonym.scatterData.x.length - 1]),
    0,
  );
}

export function buildCareerPathChart(
  synonyms: NonNullableFields<EstimatedRange>[],
  locale: I18nLocale,
): {
  ridgeLineChart: RidgeLineChart;
} {
  const minLowerBound = getMinLowerBound(synonyms);
  const maxUpperBound = getMaxUpperBound(synonyms);

  const xRangeMin = Math.floor(minLowerBound / TICK_INTERVAL) * TICK_INTERVAL;
  const xRangeMax = Math.ceil(maxUpperBound / TICK_INTERVAL) * TICK_INTERVAL;

  // @ts-expect-error TS(2322): Type '{ count: number; amount: string; description... Remove this comment to see the full error message
  const data: RidgeLineData[] = synonyms.map((synonym) => {
    const { jobDescription, salary, sectionLimits, scatterData, synonymName } =
      synonym;

    const { middle68LowerBound, middle68UpperBound } = sectionLimits;

    return {
      count: scatterData.x[scatterData.x.length - 1] - scatterData.x[0],
      amount: salary.toLocaleString(locale, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      }),
      description: jobDescription,
      name: synonymName,
      sectionLimits,
      salaryRangeLower: middle68LowerBound.toLocaleString(locale, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      }),
      salaryRangeUpper: middle68UpperBound.toLocaleString(locale, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      }),
      points: scatterData.x.map((_: number, j: number) => [
        scatterData.x[j],
        scatterData.y[j],
      ]),
      xRangeMin: Math.min(...scatterData.x),
      xRangeMax: Math.max(...scatterData.x),
      yRangeMin: Math.min(...scatterData.y),
      yRangeMax: Math.max(...scatterData.y),
      salary,
    };
  });

  const yAxisTicks = data.map((d) => d.name);

  const xGridLines = Math.min(xRangeMax, MAX_GRID_LINES);

  const numberOfTicks = 5;
  const tickInterval = (xRangeMax - xRangeMin) / (numberOfTicks - 1);

  const xAxisTicks = range(
    xRangeMin,
    xRangeMax + 1, // Add 1 to include `xRangeMax` in range
    tickInterval,
  ).map((t) =>
    t.toLocaleString(locale, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }),
  );

  const yGridLines = data.length * 2;

  const ridgeLineChart = {
    active: true,
    xRangeMin,
    xRangeMax,
    xAxisTicks,
    yAxisTicks,
    data,
    xGridLines,
    yGridLines,
  };

  return { ridgeLineChart };
}
